import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { axiosRequest } from '../utils/axiosRequest';
import toggleToast from '../utils/messaging';
import { IBookingsFilter } from '../global/interfaces';
import { IBooking } from '@homeporter/types';

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const useBookings = () => {
  const axios = axiosRequest();

  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [isFetchingBookings, setIsFetchingBookings] = useState<boolean>(false);
  const [errorFetchingBookings, setErrorFetchingBookings] =
    useState<boolean>(false);
  const [bookingsFilter, setBookingsFilter] = useState<IBookingsFilter>({});

  const fetchFilteredBookings = async (filters = {}) => {
    try {
      console.log('::::fetchFilteredBookings...', filters);

      setBookings([]);
      setIsFetchingBookings(true);
      setErrorFetchingBookings(false);
      const { data } = await axios.get('/bookings/filtered', {
        params: filters,
      });

      console.log('response:', data);

      if (data.status === 'success') {
        //check if return filter matches the setFilter
        //test if filters match

        setIsFetchingBookings(false);
        setBookings(data.bookings);
      }
    } catch (error) {
      setIsFetchingBookings(false);
      setErrorFetchingBookings(true);
    }
  };

  useEffect(() => {
    if (Object.keys(bookingsFilter).length === 0) {
      //setBookings([]);
      return;
    }

    console.log(':::useEffect bookingsFilter: ', bookingsFilter);
    fetchFilteredBookings(bookingsFilter);
  }, [bookingsFilter]);

  const fetchCurrentBookings = async () => {
    const axios = axiosRequest();

    setErrorFetchingBookings(false);
    setIsFetchingBookings(true);
    console.log('fetchCurrentBookings...');
    await sleep(250);

    const { data } = await axios.get(`/bookings/current`);
    if (data.status === 'success') {
      setIsFetchingBookings(false);

      console.log('fetchCurrentBookings data:', data.data);
      setBookings(data.data);
      return data.data;
    }

    setIsFetchingBookings(false);
    setErrorFetchingBookings(true);
    throw new Error('Failed to fetch bookings');
  };

  const { data: current, refetch: refetchCurrentBookings } = useQuery(
    'current_bookings',
    fetchCurrentBookings,
    {
      enabled: false,
    },
  );

  const fetchPastBookings = async () => {
    const axios = axiosRequest();

    setErrorFetchingBookings(false);
    setIsFetchingBookings(true);
    console.log('fetchPastBookings...');
    await sleep(700);

    const { data } = await axios.get(`/bookings/past`);
    if (data.status === 'success') {
      setIsFetchingBookings(false);
      //await new Promise(r => setTimeout(r, 250))
      console.log('fetchPastBookings data:', data.data);
      setBookings(data.data);
      return data.data;
    }

    setIsFetchingBookings(false);
    setErrorFetchingBookings(true);
    throw new Error('Failed to fetch bookings');
  };

  const { data: past, refetch: refetchPastBookings } = useQuery(
    'past_bookings',
    fetchPastBookings,
    {
      enabled: false,
    },
  );

  const sendReport = useMutation(async (payload: Record<string, any>) => {
    const mutation = async () => {
      try {
        const formData = new FormData();
        formData.append('report_url', payload.reportUrl);
        formData.append('file', payload.file);

        const { data } = await axios.post(
          `bookings/send_report/${payload.bookingId}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );

        return data;
      } catch (error) {
        console.log(`An error occurred while sending report: ${error}`);
      }
    };

    const response = await mutation();
    return response;
  });

  const { mutateAsync: updateBookingStatus, isError: updateStatusError } =
    useMutation(
      async ({ status, bookingId }: { status: string; bookingId: number }) => {
        const update = async () => {
          const axios = axiosRequest();

          console.log(`updateBookingStatus for booking_id: ${bookingId}`);
          const { data } = await axios.put(`/bookings/status/${bookingId}`, {
            status,
          });

          if (data.status === 'success') {
            console.log('update status data:', data.data);
            return data.data;
          }

          throw new Error('Failed updateBookingStatus for selected booking');
        };

        return await update();
      },
    );

  const {
    mutateAsync: updateDetails,
    isLoading: updateDetailsLoading,
    isError: updateDetailsError,
  } = useMutation(async (booking: IBooking) => {
    const update = async () => {
      const bookingParams = {
        booking_id: booking.booking_id,
        title: booking.title,
        description: booking.description,
        booking_date: booking.booking_date,
        timezone: booking.timezone,
        timezone_name: booking.timezone_name,
      };

      console.log('updatedBooking params: ', bookingParams);

      const axios = axiosRequest();
      const { data } = await axios.put(
        `/bookings/details/${booking.booking_id}`,
        {
          booking: bookingParams,
        },
      );

      if (data.status === 'success') {
        console.log('updatedBooking response data:', data);

        return data;
      }

      throw new Error('Failed updateBookingStatus for selected booking');
    };

    return await update();
  });

  const createSpotBooking = useMutation(
    async (payload: Record<string, any>) => {
      const axios = axiosRequest();
      const response = await axios.post('bookings/spot-booking', payload);
      console.log('::::createSpotBooking response:', response);

      if (response.data?.status === 'success') {
        return response.data;
      } else {
        throw new Error('An error occurred while creating spot booking');
      }
    },
  );

  const getBookingDetails = async (bookingId: number): Promise<any> => {
    console.log('::::getBookingDetails');

    try {
      const { data } = await axios.get(
        `bookings/details/${bookingId}/${Date.now()}`,
      );

      console.log('::::getBookingDetails data:', data);
      if (data.status === 'success') {
        return data;
      } else {
        throw new Error('An error occurred while fetching booking');
      }
    } catch (error: any) {
      console.error('Error fetching booking:', error);
      throw error;
    }
  };

  const updateBooking = useMutation(async (payload: Record<string, any>) => {
    console.log('::::updateBooking payload:', payload);

    const { data } = await axios.put(
      `bookings/${payload.booking_id}`,
      payload.body,
    );

    if (data.status === 'success') {
      return data;
    } else {
      throw new Error('An error occurred while updating user');
    }
  });

  const getBookingJoinFromCode = useMutation(async (joinCode: string) => {
    console.log('::::getBookingJoinFromCode joinCode:', joinCode);
    const { data } = await axios.get(`/bookings/booking-join-code/${joinCode}`);
    console.log('::::getBookingJoinFromCode data:', data);
    return data;
  });

  return {
    bookingsFilter,
    setBookingsFilter,

    bookings,
    isFetchingBookings,
    errorFetchingBookings,

    current,
    refetchCurrentBookings,

    past,
    refetchPastBookings,

    sendReport,

    updateBookingStatus,
    updateStatusError,

    updateDetails,
    updateDetailsLoading,
    updateDetailsError,

    fetchFilteredBookings,

    createSpotBooking,
    updateBooking,
    getBookingDetails,
    getBookingJoinFromCode,
  };
};

export default useBookings;
